import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Input from '../../../Components/Input/Input';
import Dropdown from '../../../Components/Dropdown/Dropdown';
import { postApi, getApi, putApi } from '../../../Services/apiService';
import { getPropertyLimitMessage, hasReachedPropertyLimit } from '../../../Utils/propertyLimit';

const isScreeningAnswerFilled = (question, value) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return false;

  if (question.type === 1) {
    const isLengthOfStay =
      question.question.toLowerCase().includes('length') &&
      question.question.toLowerCase().includes('stay');
    if (isLengthOfStay) {
      const num = parseInt(trimmed, 10);
      return !isNaN(num) && num >= 30;
    }
  }
  return true;
};

const AddProperty = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get property ID from route if in edit mode
  const isEditMode = !!id; // Check if we're in edit mode
  // Plan property limit. My Properties already hides the button once the cap is
  // hit, so this only catches someone landing on /properties/add directly.
  const [isLimitReached, setIsLimitReached] = useState(false);

  useEffect(() => {
    if (isEditMode) return;

    let cancelled = false;
    const checkPropertyLimit = async () => {
      try {
        const response = await getApi('/property/my-properties?page=1&limit=1');
        if (cancelled || response?.statusCode !== 200 || !response?.data) return;
        if (hasReachedPropertyLimit(Number(response.data.totalItems) || 0)) {
          setIsLimitReached(true);
          toast.error(getPropertyLimitMessage());
          navigate('/landlord/properties');
        }
      } catch (error) {
        console.error('Error checking property limit:', error);
      }
    };
    checkPropertyLimit();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);
  
  // Form state
  const [formData, setFormData] = useState({
    // Property Address
    address: '',
    unitApt: '',
    city: '',
    state: '',
    zipCode: '',
    latitude: null,
    longitude: null,
    
    // Property Detail
    propertyType: '',
    bedrooms: '',
    bathroom: '',
    squareFeet: '',
    monthlyRent: '',
    securityDeposit: '',
    
    
    // Description
    description: '',
    
    // Amenities (stored as array of IDs)
    amenities: [],
    
    // Tenant Screening Preferences (dynamic based on API)
    screeningAnswers: {},
    
    // Highlighted fields (starred)
    highlightedFields: [],

    // Show the landlord's Business/LLC name and logo on this property
    showLlc: false,
  });

  const [uploadedImages, setUploadedImages] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]); // Track deleted existing image IDs in edit mode
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [screeningQuestions, setScreeningQuestions] = useState([]);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(true);
  const [isLoadingAmenities, setIsLoadingAmenities] = useState(false);
  const [isLoadingProperty, setIsLoadingProperty] = useState(false);
  const [invalidFields, setInvalidFields] = useState(new Set());
  
  // Address autocomplete state
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const addressInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const isSelectingSuggestion = useRef(false); // Flag to prevent autocomplete after selection
  const isInitialAutoFill = useRef(false); // Flag to prevent autocomplete during initial auto-fill in edit mode
  const screeningFieldRefs = useRef({});
  
  // Track which address fields are disabled (initially all disabled)
  const [disabledFields, setDisabledFields] = useState({
    city: true,
    state: true,
    zipCode: true
  });

  // Create refs for form fields
  const fieldRefs = {
    images: useRef(null),
    address: useRef(null),
    unitApt: useRef(null),
    city: useRef(null),
    state: useRef(null),
    zipCode: useRef(null),
    propertyType: useRef(null),
    bedrooms: useRef(null),
    bathroom: useRef(null),
    squareFeet: useRef(null),
    monthlyRent: useRef(null),
    securityDeposit: useRef(null),
    description: useRef(null),
  };

  // Dropdown options - using enum values (1, 2, 3, 4, 5) for API
  const propertyTypeOptions = [
    { label: 'Apartment', value: '1' },
    { label: 'House', value: '2' },
    { label: 'Townhouse', value: '3' },
    { label: 'Studio', value: '4' },
    // { label: 'Other', value: '5' }
  ];
  const bedroomOptions = Array.from({ length: 20 }, (_, i) => String(i + 1));
  // 1, 1.5, 2, 2.5, … up to 20 (no 0.5)
  const bathroomOptions = Array.from({ length: 39 }, (_, i) => String((i + 2) / 2));
  const isUnitAptRequired = formData.propertyType !== '2'; // optional when Property Type is House
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let valueToSet = value;
    if (name === 'description' && value.length > 500) {
      valueToSet = value.slice(0, 500);
    }
    setFormData(prev => ({
      ...prev,
      [name]: valueToSet,
      ...(name === 'address' && !isSelectingSuggestion.current
        ? { latitude: null, longitude: null }
        : {}),
    }));
    setInvalidFields(prev => {
      const next = new Set(prev);
      next.delete(name);
      if (name === 'propertyType' && value === '2') {
        next.delete('unitApt');
      }
      return next;
    });
    
    // Handle address autocomplete
    if (name === 'address') {
      setShowSuggestions(true);
      // Debounce will be handled in useEffect
      
      // If address is cleared, reset disabled fields and clear city/state/zipCode
      if (!value || value.trim() === '') {
        setDisabledFields({
          city: true,
          state: true,
          zipCode: true
        });
        setFormData(prev => ({
          ...prev,
          city: '',
          state: '',
          zipCode: '',
          latitude: null,
          longitude: null,
        }));
      }
    }
  };
  
  // Debounced address autocomplete
  useEffect(() => {
    // Don't show suggestions if we just selected one
    if (isSelectingSuggestion.current) {
      isSelectingSuggestion.current = false;
      return;
    }
    
    // Don't trigger autocomplete during initial auto-fill in edit mode
    if (isInitialAutoFill.current) {
      return;
    }
    
    if (!formData.address || formData.address.trim().length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    const timeoutId = setTimeout(async () => {
      setIsLoadingSuggestions(true);
      try {
        const response = await getApi(`/property/autocomplete?input=${encodeURIComponent(formData.address)}`);
        if (response?.statusCode === 200 && response?.data?.suggestions) {
          setAddressSuggestions(response.data.suggestions);
          setShowSuggestions(true);
        } else {
          setAddressSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
        setAddressSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsLoadingSuggestions(false);
      }
    }, 1500); // 1.5 seconds debounce
    
    return () => clearTimeout(timeoutId);
  }, [formData.address]);
  
  // Handle address suggestion selection
  const handleAddressSelect = async (suggestion) => {
    // Set flag to prevent autocomplete from triggering
    isSelectingSuggestion.current = true;
    
    // Close suggestions immediately
    setShowSuggestions(false);
    setAddressSuggestions([]);
    
    // Update address
    setFormData(prev => ({
      ...prev,
      address: suggestion.description
    }));
    
    // Fetch place details
    try {
      const response = await postApi('/property/place-details', {
        place_id: suggestion.place_id
      });
      if (response?.statusCode === 200 && response?.data) {
        const placeData = response.data?.data;
        
        // Check which fields are available in the response
        const hasCity = !!(placeData.city);
        const hasState = !!(placeData.state);
        const hasZipCode = !!(placeData.zip_code || placeData.zipCode);
        
        const lat = placeData.latitude ?? placeData.lat;
        const lng = placeData.longitude ?? placeData.lng;

        // Auto-fill city, state, zip code, and coordinates
        setFormData(prev => ({
          ...prev,
          city: placeData.city || prev.city,
          state: placeData.state || prev.state,
          zipCode: placeData.zip_code || placeData.zipCode || placeData.zip || prev.zipCode,
          address: placeData.address || placeData.formatted_address || prev.address,
          latitude: lat != null ? Number(lat) : null,
          longitude: lng != null ? Number(lng) : null,
        }));
        
        // Update disabled fields - only enable fields that weren't returned from API
        setDisabledFields({
          city: hasCity, // Disabled if city was returned, enabled if not
          state: hasState, // Disabled if state was returned, enabled if not
          zipCode: hasZipCode // Disabled if zipCode was returned, enabled if not
        });
      } else {
        // If API call fails, enable all fields for manual input
        setDisabledFields({
          city: false,
          state: false,
          zipCode: false
        });
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
      // If API call fails, enable all fields for manual input
      setDisabledFields({
        city: false,
        state: false,
        zipCode: false
      });
    }
  };
  
  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        addressInputRef.current &&
        !addressInputRef.current.contains(event.target) &&
        !addressInputRef.current.closest('.input-wrapper')?.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    
    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  const handleAmenityChange = (amenityId) => {
    // Ensure amenityId is a string for consistent comparison
    const amenityIdStr = String(amenityId);
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.map(a => String(a)).includes(amenityIdStr)
        ? prev.amenities.filter(a => String(a) !== amenityIdStr)
        : [...prev.amenities.map(a => String(a)), amenityIdStr]
    }));
  };

  const handleScreeningQuestionChange = (questionId, value) => {
    setFormData(prev => ({
      ...prev,
      screeningAnswers: {
        ...prev.screeningAnswers,
        [questionId]: value
      }
    }));
    setInvalidFields(prev => {
      const next = new Set(prev);
      next.delete(questionId);
      return next;
    });
  };

  const handleLengthOfStayBlur = (questionKey, value) => {
    const num = parseInt(value, 10);
    if (value === '' || isNaN(num) || num < 30) {
      handleScreeningQuestionChange(questionKey, '30');
    }
  };

  const toggleHighlight = (fieldName) => {
    setFormData(prev => ({
      ...prev,
      highlightedFields: prev.highlightedFields.includes(fieldName)
        ? prev.highlightedFields.filter(f => f !== fieldName)
        : [...prev.highlightedFields, fieldName]
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const maxSize = 3 * 1024 * 1024; // 3 MB in bytes
      const validFiles = [];
      const invalidFiles = [];

      files.forEach(file => {
        // Check if file is an image
        if (!file.type.startsWith('image/')) {
          invalidFiles.push(`${file.name} is not an image file`);
          return;
        }
        
        // Check file size (3 MB = 3 * 1024 * 1024 bytes)
        if (file.size > maxSize) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
          invalidFiles.push(`${file.name} (${fileSizeMB} MB) exceeds the maximum size of 3 MB`);
          return;
        }

        validFiles.push(file);
      });

      // Show error messages for invalid files
      if (invalidFiles.length > 0) {
        invalidFiles.forEach(message => {
          toast.error(message);
        });
      }

      // Process valid files
      if (validFiles.length > 0) {
        const imageFiles = validFiles.map(file => ({
          file: file,
          url: URL.createObjectURL(file),
          id: Date.now() + Math.random()
        }));
        setUploadedImages(prev => [...prev, ...imageFiles]);
        setInvalidFields(prev => {
          const next = new Set(prev);
          next.delete('images');
          return next;
        });
      }

      // Reset input to allow selecting the same file again
      e.target.value = '';
    }
  };

  const removeImage = (id) => {
    setUploadedImages(prev => {
      const updated = prev.filter(img => img.id !== id);
      // Clean up object URLs to prevent memory leaks
      const removed = prev.find(img => img.id === id);
      if (removed) {
        // If it's an existing image from API (has isExisting flag and originalId), track it for deletion
        if (removed.isExisting && removed.originalId) {
          // This is an existing image with an API ID, add to deleted list
          setDeletedImageIds(prevDeleted => {
            if (!prevDeleted.includes(removed.originalId)) {
              return [...prevDeleted, removed.originalId];
            }
            return prevDeleted;
          });
        }
        // Only revoke URL if it's a blob URL (newly uploaded images)
        if (removed.url && removed.url.startsWith('blob:')) {
          URL.revokeObjectURL(removed.url);
        }
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;

    if (!isEditMode && isLimitReached) {
      toast.error(getPropertyLimitMessage());
      return;
    }
    
    // Build set of invalid field names and find first invalid ref for scroll/focus
    const invalid = new Set();
    let firstInvalidRef = null;
    const setInvalid = (fieldName, refOrElement) => {
      invalid.add(fieldName);
      if (!firstInvalidRef && refOrElement) {
        const el = refOrElement?.current !== undefined ? refOrElement.current : refOrElement;
        if (el) firstInvalidRef = { current: el };
      }
    };

    if (uploadedImages.length === 0) setInvalid('images', fieldRefs.images);
    if (!formData.address.trim()) setInvalid('address', fieldRefs.address);
    if (isUnitAptRequired && !formData.unitApt.trim()) setInvalid('unitApt', fieldRefs.unitApt);
    if (!formData.city.trim()) setInvalid('city', fieldRefs.city);
    if (!formData.state.trim()) setInvalid('state', fieldRefs.state);
    if (!formData.zipCode.trim()) setInvalid('zipCode', fieldRefs.zipCode);
    if (!formData.propertyType) setInvalid('propertyType', fieldRefs.propertyType);
    if (!formData.bedrooms) setInvalid('bedrooms', fieldRefs.bedrooms);
    if (!formData.bathroom) setInvalid('bathroom', fieldRefs.bathroom);

    const squareFeetNum = parseFloat(formData.squareFeet);
    if (!formData.squareFeet || isNaN(squareFeetNum) || squareFeetNum <= 0) setInvalid('squareFeet', fieldRefs.squareFeet);
    const monthlyRentNum = parseFloat(formData.monthlyRent);
    if (!formData.monthlyRent || isNaN(monthlyRentNum) || monthlyRentNum <= 0) setInvalid('monthlyRent', fieldRefs.monthlyRent);
    const securityDepositNum = parseFloat(formData.securityDeposit);
    if (!formData.securityDeposit || isNaN(securityDepositNum) || securityDepositNum <= 0) setInvalid('securityDeposit', fieldRefs.securityDeposit);
    if (!formData.description.trim()) setInvalid('description', fieldRefs.description);

    screeningQuestions.forEach((question) => {
      const questionKey = `question_${question.id}`;
      const value = formData.screeningAnswers[questionKey];
      if (!isScreeningAnswerFilled(question, value)) {
        setInvalid(questionKey, screeningFieldRefs.current[questionKey]);
      }
    });
    
    if (invalid.size > 0) {
      setInvalidFields(invalid);
      setTimeout(() => {
        if (firstInvalidRef?.current) {
          firstInvalidRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (typeof firstInvalidRef.current.focus === 'function') {
            firstInvalidRef.current.focus();
          } else if (firstInvalidRef.current.querySelector) {
            const input = firstInvalidRef.current.querySelector('input, select, textarea');
            if (input) {
              input.focus();
              const dropdownContainer = firstInvalidRef.current.querySelector('.dropdown-container');
              if (dropdownContainer) {
                dropdownContainer.dispatchEvent(new MouseEvent('click', { bubbles: true }));
              }
            }
          }
        }
      }, 100);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Extract File objects from uploadedImages (only new images, not existing ones)
      const imageFiles = uploadedImages
        .filter(imgObj => imgObj.file) // Only include images with file objects (new uploads)
        .map(imgObj => imgObj.file);
      
      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Append image files to property_image key (only new images)
      imageFiles.forEach((file) => {
        formDataToSend.append('property_image', file);
      });
      
      // In edit mode, append deleted_image_ids (property_id is in URL path)
      if (isEditMode && id) {
        // Append deleted image IDs if any images were removed
        if (deletedImageIds.length > 0) {
          formDataToSend.append('deleted_image_ids', JSON.stringify(deletedImageIds));
        }
      }
      
      // Structure property_address as JSON object
      const propertyAddress = {
        address: formData.address,
        unit_apt_number: formData.unitApt,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        ...(formData.latitude != null && formData.longitude != null && {
          latitude: formData.latitude,
          longitude: formData.longitude,
        }),
      };
      
      // Structure property_detail as JSON object
      // Convert number fields to numbers (they come as strings from inputs)
      const propertyDetail = {
        property_type: formData.propertyType,
        bedrooms: formData.bedrooms,
        bathroom: formData.bathroom,
        square_feet: formData.squareFeet ? Number(formData.squareFeet) : null,
        monthly_rent: formData.monthlyRent ? Number(formData.monthlyRent) : null,
        security_deposit: formData.securityDeposit ? Number(formData.securityDeposit) : null
      };
      
      // Structure tenant_screening_preferences as array of objects
      // Convert screening answers to the format: 
      // - For employment status: { question_id, option_id, isMark }
      // - For others: { question_id, preferred_answer, isMark }
      const tenantScreeningPreferences = Object.entries(formData.screeningAnswers)
        .filter(([key, value]) => value !== '' && value !== null && value !== undefined)
        .map(([key, value]) => {
          // Extract question ID from key (remove "question_" prefix)
          const questionId = key.replace('question_', '');
          
          // Find the question to determine its type
          const question = screeningQuestions.find(q => q.id === questionId);
          
          // Check if this question is marked/highlighted
          const isMark = formData.highlightedFields.includes(key);
          
          if (!question) {
            // Fallback if question not found
            return {
              question_id: questionId,
              preferred_answer: value,
              isMark: isMark
            };
          }
          
          // Check if this is employment status (type 2 with options)
          const isEmploymentStatus = question.type === 2 && 
            question.question.toLowerCase().includes('employment');
          
          if (isEmploymentStatus) {
            // Employment status: use option_id
            return {
              question_id: questionId,
              option_id: value,
              isMark: isMark
            };
          } else if (question.type === 3) {
            // Yes/No: Yes -> 1, No -> 0 (backend convention)
            const preferredAnswer = value === 'Yes' ? '1' : value === 'No' ? '0' : value;
            return {
              question_id: questionId,
              preferred_answer: preferredAnswer,
              isMark: isMark
            };
          } else {
            // Type 1 (input fields: Move in date, Monthly household income, Length of stay)
            // and other type 2 questions: use preferred_answer
            const isLengthOfStay =
              question.type === 1 &&
              question.question.toLowerCase().includes('length') &&
              question.question.toLowerCase().includes('stay');
            const num = parseInt(value, 10);
            const preferredAnswer =
              isLengthOfStay && (value === '' || isNaN(num) || num < 30) ? '30' : value;
            return {
              question_id: questionId,
              preferred_answer: preferredAnswer,
              isMark: isMark
            };
          }
        });
      
      // Append JSON data as strings (will be parsed on backend)
      formDataToSend.append('property_address', JSON.stringify(propertyAddress));
      formDataToSend.append('property_detail', JSON.stringify(propertyDetail));
      formDataToSend.append('description', formData.description);
      formDataToSend.append('show_llc', formData.showLlc ? 'true' : 'false');
      formDataToSend.append('amenities', JSON.stringify(formData.amenities));
      formDataToSend.append('tenant_screening_preferences', JSON.stringify(tenantScreeningPreferences));
      
      // Only append listing_duration for create mode (not in edit API spec)
      if (!isEditMode) {
        formDataToSend.append('listing_duration', '30'); // 30 days as per design
      }
      
      // Log the data structure for debugging
      console.log('Sending data:');
      console.log('Property Address:', propertyAddress);
      console.log('Property Detail:', propertyDetail);
      console.log('Description:', formData.description);
      console.log('Amenities:', formData.amenities);
      console.log('Tenant Screening Preferences:', tenantScreeningPreferences);
      console.log('Highlighted Fields:', formData.highlightedFields);
      console.log('Number of images:', imageFiles.length);
      
      // Call API with FormData (POST for create, PUT for update)
      // For edit mode, property_id is in the URL path: PUT /property/edit/{property_id}
      const apiEndpoint = isEditMode ? `/property/edit/${id}` : '/property/create';
      const apiMethod = isEditMode ? putApi : postApi;
      
      const response = await apiMethod(apiEndpoint, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Only navigate on successful response
      if (response && response.statusCode === 200) {
        toast.success(response.message || (isEditMode ? 'Property updated successfully!' : 'Property listed successfully!'));
        // Navigate back to properties list only on success
        navigate('/landlord/properties');
      } else {
        // If response exists but status is not 200, show error and stay on page
        const errorMessage = response?.message || (isEditMode ? 'Failed to update property. Please try again.' : 'Failed to list property. Please try again.');
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error submitting property:', error);
      // Show error toast and stay on the same page
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to list property. Please try again.';
        toast.error(errorMessage);
      } else if (error.request) {
        // Request was made but no response received
        toast.error('No response from server. Please check your connection and try again.');
      } else {
        // Error setting up the request
        toast.error('An error occurred. Please try again.');
      }
      // Stay on the same page - don't navigate on error
      // Stay on the same page - don't navigate on error
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/landlord/properties');
  };

  const isHighlighted = (fieldName) => {
    return formData.highlightedFields.includes(fieldName);
  };

  // Fetch property metadata on mount (no property_type) - screening questions only; amenities stay empty until type selected
  useEffect(() => {
    const fetchInitialMetadata = async () => {
      setIsLoadingMetadata(true);
      try {
        const response = await getApi('/property/metadata');
        if (response?.statusCode === 200 && response?.data) {
          // Do not set amenities here - we fetch amenities only when property type is selected
          // Filter and set active screening questions - ensure IDs are strings for consistent comparison
          const activeQuestions = (response.data.screening_questions || [])
            .filter(question => question.is_active === '1')
            .map(question => ({
              id: String(question.id),
              question: question.question,
              type: question.type,
              options: (question.options || []).map(option => ({
                ...option,
                id: String(option.id)
              }))
            }));
          setScreeningQuestions(activeQuestions);
        }
      } catch (error) {
        console.error('Error fetching property metadata:', error);
        toast.error('Failed to load property metadata. Please refresh the page.');
      } finally {
        setIsLoadingMetadata(false);
      }
    };

    fetchInitialMetadata();
  }, []);

  // Fetch amenities (and screening questions for type) when property type is selected or changed
  useEffect(() => {
    if (!formData.propertyType) {
      setAmenitiesList([]);
      return;
    }

    const fetchMetadataByPropertyType = async () => {
      setIsLoadingAmenities(true);
      try {
        const response = await getApi('/property/metadata', {
          params: { property_type: formData.propertyType }
        });
        if (response?.statusCode === 200 && response?.data) {
          const activeAmenities = (response.data.amenities || [])
            .filter(amenity => amenity.is_active === '1')
            .map(amenity => ({
              id: String(amenity.id),
              name: amenity.name
            }));
          setAmenitiesList(activeAmenities);
        }
      } catch (error) {
        console.error('Error fetching amenities for property type:', error);
        toast.error('Failed to load amenities for this property type.');
        setAmenitiesList([]);
      } finally {
        setIsLoadingAmenities(false);
      }
    };

    fetchMetadataByPropertyType();
  }, [formData.propertyType]);

  // Default "Length of stay" to 30 in create mode when screening questions are loaded
  useEffect(() => {
    if (!screeningQuestions.length || isEditMode) return;
    const lengthOfStayQ = screeningQuestions.find(
      (q) => q.question.toLowerCase().includes('length') && q.question.toLowerCase().includes('stay')
    );
    if (!lengthOfStayQ) return;
    const key = `question_${lengthOfStayQ.id}`;
    setFormData((prev) => {
      if (prev.screeningAnswers[key] !== undefined && prev.screeningAnswers[key] !== '') return prev;
      return { ...prev, screeningAnswers: { ...prev.screeningAnswers, [key]: '30' } };
    });
  }, [screeningQuestions, isEditMode]);

  // Fetch property data in edit mode
  useEffect(() => {
    const fetchPropertyData = async () => {
      if (!isEditMode || !id || isLoadingMetadata) return;
      
      setIsLoadingProperty(true);
      // Set flag to prevent autocomplete during initial auto-fill
      isInitialAutoFill.current = true;
      
      try {
        const response = await getApi(`/property/${id}`);
        
        if (response?.statusCode === 200 && response?.data) {
          const propertyData = response.data;
          
          // Parse amenities - can be array of objects with id/name OR array of IDs OR JSON string
          let amenities = [];
          try {
            if (propertyData.amenities) {
              let parsedAmenities = typeof propertyData.amenities === 'string' 
                ? JSON.parse(propertyData.amenities) 
                : propertyData.amenities;
              
              if (Array.isArray(parsedAmenities)) {
                // Check if it's an array of objects (with id property) or array of IDs
                if (parsedAmenities.length > 0 && typeof parsedAmenities[0] === 'object' && parsedAmenities[0].id) {
                  // Array of objects: extract IDs
                  amenities = parsedAmenities.map(amenity => String(amenity.id));
                } else {
                  // Array of IDs: convert to strings
                  amenities = parsedAmenities.map(id => String(id));
                }
              }
            }
          } catch (error) {
            console.error('Error parsing amenities:', error);
            amenities = [];
          }
          
          // Parse property address (if it's stored as JSON)
          let addressData = {};
          try {
            if (propertyData.property_address) {
              addressData = typeof propertyData.property_address === 'string'
                ? JSON.parse(propertyData.property_address)
                : propertyData.property_address;
            }
          } catch (error) {
            console.error('Error parsing property_address:', error);
          }
          
          // Parse property detail (if it's stored as JSON)
          let detailData = {};
          try {
            if (propertyData.property_detail) {
              detailData = typeof propertyData.property_detail === 'string'
                ? JSON.parse(propertyData.property_detail)
                : propertyData.property_detail;
            }
          } catch (error) {
            console.error('Error parsing property_detail:', error);
          }
          
          // Parse tenant screening preferences from landlord_preferences
          let screeningAnswers = {};
          let highlightedFields = [];
          if (propertyData.landlord_preferences && Array.isArray(propertyData.landlord_preferences)) {
            propertyData.landlord_preferences.forEach((pref) => {
              // Ensure question_id is a string to match metadata format
              const questionId = String(pref.question_id);
              const questionKey = `question_${questionId}`;
              
              // Find the question to determine its type
              const question = screeningQuestions.find(q => String(q.id) === questionId);
              
              let answerValue = '';
              
              // If option_id exists, use it (for employment status and other type 2 questions with options)
              if (pref.option_id !== undefined && pref.option_id !== null) {
                answerValue = String(pref.option_id);
              } 
              // If preferred_answer exists, use it (for input fields and yes/no questions)
              else if (pref.preferred_answer !== undefined && pref.preferred_answer !== null) {
                const preferredAnswer = String(pref.preferred_answer);
                
                // Yes/No (type 3): 1 -> Yes, 0 -> No
                if (question && question.type === 3) {
                  const n = Number(preferredAnswer);
                  const isOne = preferredAnswer === '1' || n === 1;
                  const isZero = preferredAnswer === '0' || n === 0;
                  answerValue = isOne ? 'Yes' : isZero ? 'No' : preferredAnswer;
                } else {
                  // For input fields (type 1), use the value as is
                  answerValue = preferredAnswer;
                }
              }
              
              if (answerValue) {
                screeningAnswers[questionKey] = answerValue;
              }
              
              // Check if this question is marked (is_mark field)
              if (pref.is_mark === true) {
                highlightedFields.push(questionKey);
              }
            });
          }
          
          // Load existing images
          const existingImages = propertyData.property_images && propertyData.property_images.length > 0
            ? propertyData.property_images.map((img, index) => ({
                file: null, // No file object for existing images
                url: img.image_url,
                id: img.id || `existing-${index}`, // Use API image ID if available
                isExisting: true,
                originalId: img.id // Store original ID for tracking deletions
              }))
            : [];
          
          // Pre-fill form data
          setFormData({
            address: propertyData.address || addressData.address || '',
            unitApt: propertyData.unit_number || addressData.unit_apt_number || '',
            city: propertyData.city || addressData.city || '',
            state: propertyData.state || addressData.state || '',
            zipCode: propertyData.zip_code || addressData.zip_code || '',
            latitude: addressData.latitude ?? propertyData.latitude ?? null,
            longitude: addressData.longitude ?? propertyData.longitude ?? null,
            propertyType: propertyData.property_type || detailData.property_type || '',
            bedrooms: String(propertyData.bedrooms || detailData.bedrooms || ''),
            bathroom: String(propertyData.bathrooms || detailData.bathroom || ''),
            squareFeet: String(propertyData.square_feet || detailData.square_feet || ''),
            monthlyRent: String(propertyData.monthly_rent || detailData.monthly_rent || ''),
            securityDeposit: String(propertyData.security_deposit || detailData.security_deposit || ''),
            description: propertyData.description || '',
            showLlc: propertyData.show_llc === true || propertyData.show_llc === 'true' || propertyData.show_llc === 1,
            amenities: amenities,
            screeningAnswers: screeningAnswers,
            highlightedFields: highlightedFields,
          });
          
          // Set existing images
          setUploadedImages(existingImages);
          // Reset deleted image IDs when loading property data
          setDeletedImageIds([]);
          
          // Update disabled fields based on address data
          setDisabledFields({
            city: !!(propertyData.city || addressData.city),
            state: !!(propertyData.state || addressData.state),
            zipCode: !!(propertyData.zip_code || addressData.zip_code),
          });
          
          // Reset the initial auto-fill flag after a short delay to allow future autocomplete
          setTimeout(() => {
            isInitialAutoFill.current = false;
          }, 500);
        } else {
          toast.error(response?.message || 'Failed to load property data');
          navigate('/landlord/properties');
          isInitialAutoFill.current = false;
        }
      } catch (error) {
        console.error('Error fetching property data:', error);
        toast.error('Failed to load property data. Please try again.');
        navigate('/landlord/properties');
        isInitialAutoFill.current = false;
      } finally {
        setIsLoadingProperty(false);
      }
    };

    fetchPropertyData();
  }, [isEditMode, id, isLoadingMetadata, navigate]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      // Clean up all object URLs when component unmounts
      uploadedImages.forEach(imageObj => {
        if (imageObj && imageObj.url) {
          URL.revokeObjectURL(imageObj.url);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run cleanup on unmount

  return (
    <div className="add-property-page">
      <form onSubmit={handleSubmit} className="add-property-form" noValidate>
        {/* Property Images Section */}
        <div className="add-property-section" ref={fieldRefs.images}>
          <h2 className="add-property-section-title">Property Images <span className="required">*</span></h2>
          <div className="property-images-wrapper">
            <div className="property-images-container">
              {/* Display uploaded images */}
              {uploadedImages.map((imageObj) => (
                <div key={imageObj.id} className="property-image-preview-item">
                  <img src={imageObj.url} alt="Property" />
                  <button
                    type="button"
                    className="property-image-remove"
                    onClick={() => removeImage(imageObj.id)}
                    aria-label="Remove image"
                  >
                    ×
                  </button>
                </div>
              ))}
              
              {/* Upload area - always visible */}
              <div className={`property-images-upload-area ${invalidFields.has('images') ? 'property-images-upload-area-invalid' : ''}`}>
                <input
                  type="file"
                  id="image-upload"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="property-images-input"
                />
                <label htmlFor="image-upload" className="property-images-upload-label">
                  <div className="property-images-upload-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="property-images-upload-text">Upload Images</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Business/LLC Visibility */}
        <div className="add-property-section">
          <div className="property-llc-form">
            <label className="property-llc-toggle">
              <span className="property-llc-toggle-text">
                <span className="property-llc-toggle-title">Show Business/LLC</span>
                <span className="property-llc-toggle-hint">Display your Business/LLC name and logo from your profile on this property</span>
              </span>
              <input
                type="checkbox"
                checked={formData.showLlc}
                onChange={(e) => setFormData(prev => ({ ...prev, showLlc: e.target.checked }))}
              />
              <span className="property-llc-toggle-slider" aria-hidden="true" />
            </label>
          </div>
        </div>

        {/* Property Address Section */}
        <div className="add-property-section">
          <h2 className="add-property-section-title">Property Address <span className="required">*</span></h2>
          <div className="property-address-form">
            <div className="property-address-row property-address-row-full">
              <div style={{ position: 'relative', width: '100%' }}>
                <Input
                  ref={(node) => {
                    fieldRefs.address.current = node;
                    addressInputRef.current = node;
                  }}
                  name="address"
                  label="Address"
                  required
                  invalid={invalidFields.has('address')}
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter address"
                  autoComplete="off"
                />
                {showSuggestions && addressSuggestions.length > 0 && (
                  <div
                    ref={suggestionsRef}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      zIndex: 1000,
                      maxHeight: '300px',
                      overflowY: 'auto',
                      marginTop: '4px'
                    }}
                  >
                    {isLoadingSuggestions ? (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                        Loading suggestions...
                      </div>
                    ) : (
                      addressSuggestions.map((suggestion, index) => (
                        <div
                          key={suggestion.place_id || index}
                          onClick={() => handleAddressSelect(suggestion)}
                          style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            borderBottom: index < addressSuggestions.length - 1 ? '1px solid #eee' : 'none',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f5f5f5';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'white';
                          }}
                        >
                          <div style={{ fontWeight: '500', color: '#333', marginBottom: '4px' }}>
                            {suggestion.main_text}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {suggestion.description}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="property-address-row">
              <Input
                ref={fieldRefs.unitApt}
                name="unitApt"
                label="Unit/Apt. Number"
                required={isUnitAptRequired}
                invalid={invalidFields.has('unitApt')}
                value={formData.unitApt}
                onChange={handleInputChange}
                placeholder="Enter unit/apt number"
              />
              <Input
                ref={fieldRefs.city}
                name="city"
                label="City"
                required
                invalid={invalidFields.has('city')}
                value={formData.city}
                onChange={handleInputChange}
                placeholder="Enter city"
                disabled={disabledFields.city}
              />
            </div>
            <div className="property-address-row">
              <Input
                ref={fieldRefs.state}
                name="state"
                label="State"
                required
                invalid={invalidFields.has('state')}
                value={formData.state}
                onChange={handleInputChange}
                placeholder="Enter state"
                disabled={disabledFields.state}
              />
              <Input
                ref={fieldRefs.zipCode}
                name="zipCode"
                label="Zip Code"
                required
                invalid={invalidFields.has('zipCode')}
                value={formData.zipCode}
                onChange={handleInputChange}
                placeholder="Enter zip code"
                disabled={disabledFields.zipCode}
              />
            </div>
          </div>
        </div>

        {/* Property Detail Section */}
        <div className="add-property-section">
          <h2 className="add-property-section-title">Property Detail <span className="required">*</span></h2>
          <div className="property-detail-form">
            <div className="property-detail-row">
              <div ref={fieldRefs.propertyType}>
                <Dropdown
                  name="propertyType"
                  label="Property Type"
                  required
                  invalid={invalidFields.has('propertyType')}
                  value={formData.propertyType}
                  onChange={handleInputChange}
                  options={propertyTypeOptions}
                  placeholder="Select property type"
                />
              </div>
              <div ref={fieldRefs.bedrooms}>
                <Dropdown
                  name="bedrooms"
                  label="Bedrooms"
                  required
                  invalid={invalidFields.has('bedrooms')}
                  value={formData.bedrooms}
                  onChange={handleInputChange}
                  options={bedroomOptions}
                  placeholder="Select number of bedrooms"
                />
              </div>
              <div ref={fieldRefs.bathroom}>
                <Dropdown
                  name="bathroom"
                  label="Bathroom"
                  required
                  invalid={invalidFields.has('bathroom')}
                  value={formData.bathroom}
                  onChange={handleInputChange}
                  options={bathroomOptions}
                  placeholder="Select number of bathrooms"
                />
              </div>
            </div>
            <div className="property-detail-row">
              <Input
                ref={fieldRefs.squareFeet}
                type="number"
                name="squareFeet"
                label="Square feet"
                required
                invalid={invalidFields.has('squareFeet')}
                value={formData.squareFeet}
                onChange={handleInputChange}
                placeholder="Enter square feet"
                step="0.01"
                minValue={0}
              />
              <Input
                ref={fieldRefs.monthlyRent}
                type="number"
                name="monthlyRent"
                label="Monthly Rent ($)"
                required
                invalid={invalidFields.has('monthlyRent')}
                value={formData.monthlyRent}
                onChange={handleInputChange}
                placeholder="Enter monthly rent"
                step="0.01"
                minValue={0}
              />
              <Input
                ref={fieldRefs.securityDeposit}
                type="number"
                name="securityDeposit"
                label="Security Deposit"
                required
                invalid={invalidFields.has('securityDeposit')}
                value={formData.securityDeposit}
                onChange={handleInputChange}
                placeholder="Enter security deposit"
                step="0.01"
                minValue={0}
              />
            </div>
          </div>
        </div>

        {/* Description Section */}
        <div className="add-property-section">
          <h2 className="add-property-section-title">Description <span className="required">*</span></h2>
          <div className="property-description-form">
            <label className="property-description-label">Property Description <span className="required">*</span></label>
            <textarea
              ref={fieldRefs.description}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe your property, highlight key features, nearby amenities, transportation options..."
              className={`property-description-textarea ${invalidFields.has('description') ? 'property-description-textarea-invalid' : ''}`}
              rows="6"
              maxLength={500}
            />
            <div className="property-description-counter">
              {formData.description.length}/500
            </div>
          </div>
        </div>

        {/* Amenities Section */}
        <div className="add-property-section">
          <h2 className="add-property-section-title">Amenities <span className="required">*</span></h2>
          {isLoadingMetadata ? (
            <div className="property-amenities-form">
              <p>Loading...</p>
            </div>
          ) : !formData.propertyType ? (
            <div className="property-amenities-form">
              <p>Need to select property type</p>
            </div>
          ) : isLoadingAmenities ? (
            <div className="property-amenities-form">
              <p>Loading amenities...</p>
            </div>
          ) : (
            <div className="property-amenities-form">
              {amenitiesList.length > 0 ? (
                amenitiesList.map((amenity) => (
                  <label key={amenity.id} className="property-amenity-item">
                    <input
                      type="checkbox"
                      checked={formData.amenities.map(a => String(a)).includes(String(amenity.id))}
                      onChange={() => handleAmenityChange(amenity.id)}
                      className="property-amenity-checkbox"
                    />
                    <span className="property-amenity-label">{amenity.name}</span>
                  </label>
                ))
              ) : (
                <p>No amenities available</p>
              )}
            </div>
          )}
        </div>

        {/* Tenant Screening Preferences Section */}
        <div className="add-property-section">
          <h2 className="add-property-section-title">Tenant Screening Preferences <span className="required">*</span></h2>
          {isLoadingMetadata ? (
            <div className="tenant-screening-form">
              <p>Loading screening questions...</p>
            </div>
          ) : (
            <div className="tenant-screening-form">
              {screeningQuestions.length > 0 ? (
                <div className="tenant-screening-row">
                  {screeningQuestions.map((question) => {
                    const questionKey = `question_${question.id}`;
                    const isHighlightedField = isHighlighted(questionKey);
                    const currentValue = formData.screeningAnswers[questionKey] || '';

                    // Render based on question type
                    // type 1 = input (date, number, text)
                    // type 2 = radio buttons
                    // type 3 = yes/no (radio buttons with Yes/No)
                    const renderQuestionInput = () => {
                      if (question.type === 1) {
                        // Input field - determine input type based on question
                        const isDateField = question.question.toLowerCase().includes('date');
                        const isLengthOfStay =
                          question.question.toLowerCase().includes('length') &&
                          question.question.toLowerCase().includes('stay');
                        const isNumberField =
                          question.question.toLowerCase().includes('income') ||
                          isLengthOfStay ||
                          question.question.toLowerCase().includes('length');
                        const today = new Date().toISOString().split('T')[0];

                        return (
                          <input
                            type={isDateField ? 'date' : isNumberField ? 'number' : 'text'}
                            name={questionKey}
                            value={isLengthOfStay ? (currentValue || '30') : currentValue}
                            min={isDateField ? today : isLengthOfStay ? 30 : undefined}
                            onChange={(e) =>
                              handleScreeningQuestionChange(questionKey, e.target.value)
                            }
                            onBlur={
                              isLengthOfStay
                                ? (e) =>
                                    handleLengthOfStayBlur(questionKey, e.target.value)
                                : undefined
                            }
                            placeholder={`Enter ${question.question.toLowerCase()}`}
                            className="input-field"
                          />
                        );
                      } else if (question.type === 2) {
                        // Radio buttons with options
                        return (
                          <div className="tenant-screening-radio-group">
                            {question.options.map((option) => (
                              <label key={option.id} className="tenant-screening-radio-item">
                                <input
                                  type="radio"
                                  name={questionKey}
                                  value={option.id}
                                  checked={String(currentValue) === String(option.id)}
                                  onChange={(e) => handleScreeningQuestionChange(questionKey, e.target.value)}
                                />
                                <span>{option.option_name}</span>
                              </label>
                            ))}
                          </div>
                        );
                      } else if (question.type === 3) {
                        // Yes/No radio buttons
                        return (
                          <div className="tenant-screening-radio-group">
                            <label className="tenant-screening-radio-item">
                              <input
                                type="radio"
                                name={questionKey}
                                value="Yes"
                                checked={currentValue === 'Yes'}
                                onChange={(e) => handleScreeningQuestionChange(questionKey, e.target.value)}
                              />
                              <span>Yes</span>
                            </label>
                            <label className="tenant-screening-radio-item">
                              <input
                                type="radio"
                                name={questionKey}
                                value="No"
                                checked={currentValue === 'No'}
                                onChange={(e) => handleScreeningQuestionChange(questionKey, e.target.value)}
                              />
                              <span>No</span>
                            </label>
                          </div>
                        );
                      }
                      return null;
                    };

                    const isLengthOfStayLabel =
                      question.question.toLowerCase().includes('length') &&
                      question.question.toLowerCase().includes('stay');
                    const fieldLabel = isLengthOfStayLabel
                      ? 'Length of stay (in days)'
                      : question.question;

                    return (
                      <div
                        key={question.id}
                        ref={(el) => { screeningFieldRefs.current[questionKey] = el; }}
                        className={`tenant-screening-field${invalidFields.has(questionKey) ? ' tenant-screening-field-invalid' : ''}`}
                      >
                        <div className="tenant-screening-field-header">
                          <label className="tenant-screening-label">{fieldLabel} <span className="required">*</span></label>
                          <button
                            type="button"
                            className="tenant-screening-star"
                            onClick={() => toggleHighlight(questionKey)}
                          >
                            <img
                              src={isHighlightedField ? '/assets/images/star_filled.png' : '/assets/images/star_unfilled.png'}
                              alt="Highlight"
                            />
                          </button>
                        </div>
                        {renderQuestionInput()}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p>No screening questions available</p>
              )}
            </div>
          )}
          <div className="tenant-screening-instruction">
            <img src="/assets/images/star_filled.png" alt="Star" className="tenant-screening-instruction-star" />
            <span>Mark the questions that matter most to you. Highlighted questions will be prioritized in tenant evaluation.</span>
          </div>
        </div>

        {/* Listing Duration Notice */}
        <div className="listing-duration-notice">
          <div className="listing-duration-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="listing-duration-content">
            <h3 className="listing-duration-title">Listing Duration: 30 Days</h3>
            <p className="listing-duration-description">
              Your property listing will be active for 30 days. You can relist it after expiration. Tenants will be able to view the listing and start the AI screening process.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="add-property-actions">
          <button type="button" className="add-property-cancel-btn" onClick={handleCancel}>
            Cancel
          </button>
          <button 
            type="submit" 
            className="add-property-submit-btn"
            disabled={isSubmitting}
          >
            {isEditMode ? 'Update Property' : 'List Property'}
          </button>
        </div>
      </form>

      {/* Blocking centered loader shown while the submit request is in flight */}
      {isSubmitting && (
        <div className="add-property-loader-overlay" role="alert" aria-live="assertive" aria-busy="true">
          <div className="add-property-loader-card">
            <div className="add-property-loader-spinner" aria-hidden="true">
              <span className="add-property-loader-ring" />
              <span className="add-property-loader-ring add-property-loader-ring-inner" />
              <img
                src={window.getPublicUrl ? window.getPublicUrl('/assets/images/Logo.png') : '/assets/images/Logo.png'}
                alt=""
                className="add-property-loader-logo"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
            <h3 className="add-property-loader-title">
              {isEditMode ? 'Updating your property' : 'Listing your property'}
            </h3>
            <p className="add-property-loader-text">
              Uploading images and saving details. Please don&apos;t close this window.
            </p>
            <div className="add-property-loader-bar" aria-hidden="true">
              <span />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddProperty;
